// docx-preview leaves overflowing tables on one very tall page. Reflow the
// rendered blocks into preview pages, retaining every row and paragraph.
export function paginateWordTables(preview: HTMLElement) {
  preview.style.transform = "none";
  for (const section of preview.querySelectorAll<HTMLElement>("section.docx")) {
    if (section.dataset.paginated) continue;
    section.dataset.paginated = "true";
    const article = section.querySelector<HTMLElement>(":scope > article");
    if (!article) continue;
    const style = getComputedStyle(section);
    const pageHeight = Number.parseFloat(style.minHeight);
    const available = pageHeight - Number.parseFloat(style.paddingTop) - Number.parseFloat(style.paddingBottom) - 24;
    // Some Word tables extend past their saved paper margins. Include the
    // whole table in the preview instead of clipping the Friday column.
    const contentWidth = Math.max(article.offsetWidth, ...[...article.children].map(child => (child as HTMLElement).offsetWidth));
    section.style.width = `${Math.max(section.offsetWidth, contentWidth + Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight))}px`;
    if (!Number.isFinite(available) || available < 300 || article.offsetHeight <= available) continue;

    type Block = { source: HTMLElement; rows?: HTMLTableRowElement[]; height: number; keepWithNext?: boolean };
    const blocks: Block[] = [];
    for (const child of article.children) {
      if (!(child instanceof HTMLTableElement)) {
        const element = child as HTMLElement;
        const childStyle = getComputedStyle(element);
        blocks.push({ source: element, height: element.offsetHeight + Number.parseFloat(childStyle.marginTop) + Number.parseFloat(childStyle.marginBottom) });
        continue;
      }
      const rows = [...child.rows];
      for (let start = 0; start < rows.length;) {
        // A vertical merge must stay with all the rows it spans.
        let end = start + 1;
        for (let index = start; index < end && index < rows.length; index++) {
          for (const cell of rows[index]!.cells) end = Math.max(end, cell.rowSpan === 0 ? rows.length : index + cell.rowSpan);
        }
        const group = rows.slice(start, end);
        const text = document.createTreeWalker(group[group.length - 1]!, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        let hasText = false;
        let heading = true;
        while ((node = text.nextNode())) {
          if (!node.textContent?.trim()) continue;
          hasText = true;
          if (Number.parseInt(getComputedStyle(node.parentElement!).fontWeight) < 600) heading = false;
        }
        blocks.push({ source: child, rows: group, height: group.reduce((height, row) => height + row.offsetHeight, 0), keepWithNext: hasText && heading });
        start = end;
      }
    }
    const groups: Block[][] = [];
    let group: Block[] = [];
    let height = 0;
    for (const [index, block] of blocks.entries()) {
      // Do not leave a table heading (for example, the week's dates) alone
      // at the foot of the preceding page.
      let required = block.height;
      for (let next = index; blocks[next]?.keepWithNext && blocks[next + 1]; next++) required += blocks[next + 1]!.height;
      if (group.length > 0 && height + required > available) { groups.push(group); group = []; height = 0; }
      group.push(block); height += block.height;
    }
    if (group.length) groups.push(group);
    if (groups.length < 2) continue;
    for (const group of groups) {
      const page = section.cloneNode(true) as HTMLElement;
      const body = page.querySelector<HTMLElement>(":scope > article")!;
      body.replaceChildren();
      let sourceTable: HTMLElement | undefined;
      let targetTable: HTMLTableElement | undefined;
      for (const block of group) {
        if (!block.rows) {
          body.append(block.source.cloneNode(true));
          sourceTable = undefined;
          continue;
        }
        if (sourceTable !== block.source) {
          sourceTable = block.source;
          targetTable = block.source.cloneNode(true) as HTMLTableElement;
          for (const row of [...targetTable.rows]) row.remove();
          body.append(targetTable);
        }
        const targetBody = targetTable!.tBodies[0] ?? targetTable!;
        for (const row of block.rows) targetBody.append(row.cloneNode(true));
      }
      section.before(page);
    }
    section.remove();
  }
}
